import { getLessonAssetFolder } from 'helpers/lessonAssetPaths.helpers';

export const getLessonById = (currentCourse: any, lessonId: string) => {
  const modules = currentCourse.modules;

  for (const module of modules) {
    const lesson = module.lessons.find(
      (lessonItem: any) => lessonItem.id === lessonId,
    );

    if (lesson) {
      return lesson;
    }
  }

  return null;
};

export const convertTheoryLessonToApi = (
  lesson: any,
  courseId: string,
  moduleId: string,
  imagesData: any,
) => ({
  blocks: imagesData.blocks,
  courseId,
  moduleId,
  free: lesson.free,
  order: lesson.order,
  soon: lesson.soon,
  status: lesson.status,
  title: lesson.title,
  type: lesson.type,
});

export const convertFigmaLessonToApi = (
  lesson: any,
  courseId: string,
  moduleId: string,
  imagesData: any,
) => ({
  courseId,
  moduleId,
  free: lesson.free,
  order: lesson.order,
  soon: lesson.soon,
  status: lesson.status,
  title: lesson.title,
  type: lesson.type,
  description: lesson.description,
  designType: lesson.designType,
  fileId: lesson.fileId,
  filePageId: lesson.filePageId,
  goodToKnowBlocks: lesson.goodToKnowBlocks,
  canvasTheory: imagesData.canvasTheory,
  initialState: imagesData.initialState ?? lesson.initialState,
  kind: lesson.kind,
  percentToComplete: lesson.percentToComplete,
  resultPreview: imagesData.resultPreview,
  resultRefPreview: lesson.resultRefPreview,
  tasks: imagesData.tasks ?? lesson.tasks,
});

const getFileNameFromAssetPath = (assetPath: string) => {
  const pathParts = assetPath.split('/');

  return pathParts[pathParts.length - 1];
};

const getLessonFolderFromAssetPath = (assetPath: string) => {
  const pathParts = assetPath.replace(/^\//, '').split('/');

  if (pathParts[0] === 's3' && pathParts[1] === 'lessons' && pathParts[2]) {
    return `lessons/${pathParts[2]}`;
  }

  return null;
};

export const getTheoryLessonImages = (lesson: any, _course?: any) => {
  const lessonAssetFolder = getLessonAssetFolder(lesson.title, lesson.id);

  const blocksImages = lesson.blocks
    .filter((block: any) => block.type === 'image')
    .map((block: any) => block.data)
    .map(({ url }: { url: any }) => {
      const fileName = getFileNameFromAssetPath(url);

      return {
        sourceFileKey: url.replace('/', ''),
        targetFileKey: `s3/${lessonAssetFolder}/${fileName}`,
      };
    });

  return {
    blocksImages,
    blocks: lesson.blocks.map((block: any) => {
      if (block.type === 'image') {
        const fileName = getFileNameFromAssetPath(block.data.url);

        return {
          ...block,
          data: {
            ...block.data,
            url: `/s3/${lessonAssetFolder}/${fileName}`,
          },
        };
      }

      return block;
    }),
  };
};

export const getFigmaLessonImages = (
  lesson: any,
  _course?: any,
  _courseModule?: any,
) => {
  const targetLessonFolder = getLessonAssetFolder(lesson.title, lesson.id);
  const sourceLessonFolder =
    getLessonFolderFromAssetPath(lesson.resultPreview) ??
    getLessonFolderFromAssetPath(lesson.canvasTheory?.[0]?.url) ??
    targetLessonFolder;

  const canvasTheoryImages = lesson.canvasTheory.map(
    ({ url }: { url: any }) => {
      const fileName = getFileNameFromAssetPath(url);

      return {
        sourceFileKey: url.replace('/', ''),
        targetFileKey: `s3/${targetLessonFolder}/${fileName}`,
      };
    },
  );

  const resultPreviewImage = {
    sourceFileKey: lesson.resultPreview.replace('/', ''),
    targetFileKey: `s3/${targetLessonFolder}/result-preview.jpg`,
  };

  const initialStateImages = getImagesKeys(lesson.initialState.layers).map(
    (imageKey: any) => ({
      sourceFileKey: `s3/${sourceLessonFolder}/${imageKey}.jpg`,
      targetFileKey: `s3/${targetLessonFolder}/${imageKey}.jpg`,
    }),
  );

  const taskPreviewImages = (lesson.tasks || [])
    .filter((task: any) => task?.preview)
    .map((task: any) => {
      const fileName = getFileNameFromAssetPath(task.preview);

      return {
        sourceFileKey: task.preview.replace(/^\//, ''),
        targetFileKey: `s3/${targetLessonFolder}/${fileName}`,
      };
    });

  const initialStatePreviewImage = lesson.initialState?.preview
    ? {
        sourceFileKey: lesson.initialState.preview.replace(/^\//, ''),
        targetFileKey: `s3/${targetLessonFolder}/${getFileNameFromAssetPath(lesson.initialState.preview)}`,
      }
    : null;

  const updatedTasks = (lesson.tasks || []).map((task: any) => {
    if (!task?.preview) {
      return task;
    }

    const fileName = getFileNameFromAssetPath(task.preview);

    return {
      ...task,
      preview: `/s3/${targetLessonFolder}/${fileName}`,
    };
  });

  const updatedInitialState = lesson.initialState
    ? {
        ...lesson.initialState,
        preview: initialStatePreviewImage
          ? `/${initialStatePreviewImage.targetFileKey}`
          : lesson.initialState.preview,
      }
    : lesson.initialState;

  return {
    canvasTheoryImages: canvasTheoryImages,
    canvasTheory: canvasTheoryImages.map((item: any) => ({
      url: `/${item.targetFileKey}`,
    })),
    resultPreviewImage,
    resultPreview: `/${resultPreviewImage.targetFileKey}`,
    initialStateImages,
    taskPreviewImages,
    initialStatePreviewImage,
    tasks: updatedTasks,
    initialState: updatedInitialState,
  };
};

export const getImagesKeys = (layers: any): any => {
  const imagesKeys: any[] = [];

  for (const layer of layers) {
    const hasImageFill = layer.properties.fills.some(
      (fill: any) => fill.type === 'IMAGE',
    );

    if (hasImageFill) {
      imagesKeys.push(
        ...layer.properties.fills
          .filter((fill: any) => fill.type === 'IMAGE')
          .map((fill: any) => fill.imageHash),
      );
    }

    if (layer.children && layer.children.length) {
      imagesKeys.push(...getImagesKeys(layer.children));
    }
  }

  return imagesKeys;
};
